/**
 * Images a tool returns (a screenshot, a generated photo) shown to the user under the step that
 * made them. Every agent backend finds them in its own result shape (MCP `{ type: 'image', data,
 * mimeType }`, Anthropic `{ type: 'image', source: { type: 'base64', media_type, data } }`, ACP
 * tool content, OpenCode file attachments) and writes them as `file` parts that name their tool
 * call; the tool's own output keeps a short placeholder instead of the base64.
 */

/** An image of a tool result: its media type and its base64 data. */
export type ToolResultImage = { mediaType: string; data: string };

/** Raster images a browser shows as they are; anything else stays in the output. */
const IMAGE_TYPES: ReadonlySet<string> = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
]);
/** At most this many images per tool call reach the transcript. */
export const TOOL_IMAGES_PER_CALL = 8;
/** An image larger than this (base64 characters, about 3 MB of bytes) is not shown. */
export const TOOL_IMAGE_MAX_BASE64 = 4 * 1024 * 1024;
/** What a tool's output keeps in place of an image's base64. */
export const TOOL_IMAGE_PLACEHOLDER = '[image shown in the transcript]';
const MAX_DEPTH = 6;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const DATA_URL = /^data:(image\/[a-z0-9.+-]+);base64,(.*)$/is;

/** The image a value describes, in one of the shapes tools return, or null. */
function imageOf(value: Record<string, unknown>): ToolResultImage | null {
  if (value['type'] === 'image') {
    if (typeof value['data'] === 'string' && typeof value['mimeType'] === 'string')
      return { mediaType: value['mimeType'].toLowerCase(), data: value['data'] };
    const source = value['source'];
    if (
      isRecord(source) &&
      source['type'] === 'base64' &&
      typeof source['data'] === 'string' &&
      typeof source['media_type'] === 'string'
    )
      return { mediaType: source['media_type'].toLowerCase(), data: source['data'] };
  }
  if (value['type'] === 'file' && typeof value['url'] === 'string') {
    const match = DATA_URL.exec(value['url']);
    if (match) return { mediaType: (match[1] as string).toLowerCase(), data: match[2] as string };
  }
  return null;
}

function usable(image: ToolResultImage): boolean {
  return (
    IMAGE_TYPES.has(image.mediaType) &&
    image.data.length > 0 &&
    image.data.length <= TOOL_IMAGE_MAX_BASE64
  );
}

/** The images in a tool's result, in order, without duplicates and within the limits. */
export function toolResultImages(...values: readonly unknown[]): ToolResultImage[] {
  const images: ToolResultImage[] = [];
  const seen = new Set<string>();
  const visit = (value: unknown, depth: number): void => {
    if (depth > MAX_DEPTH || images.length >= TOOL_IMAGES_PER_CALL) return;
    if (Array.isArray(value)) {
      for (const item of value) visit(item, depth + 1);
      return;
    }
    if (!isRecord(value)) return;
    const image = imageOf(value);
    if (image !== null) {
      const key = `${image.mediaType}:${image.data.length}:${image.data.slice(0, 64)}`;
      if (usable(image) && !seen.has(key)) {
        seen.add(key);
        images.push(image);
      }
      return;
    }
    for (const item of Object.values(value)) visit(item, depth + 1);
  };
  for (const value of values) visit(value, 0);
  return images;
}

/** A tool's output without the base64 of its images (a copy; the value is left untouched). */
export function withoutToolImageData<T>(value: T): T {
  const strip = (item: unknown, depth: number): unknown => {
    if (depth > MAX_DEPTH) return item;
    if (Array.isArray(item)) return item.map((entry) => strip(entry, depth + 1));
    if (!isRecord(item)) return item;
    const image = imageOf(item);
    if (image !== null) {
      if (item['type'] === 'file') return { ...item, url: TOOL_IMAGE_PLACEHOLDER };
      if (typeof item['data'] === 'string') return { ...item, data: TOOL_IMAGE_PLACEHOLDER };
      return { ...item, source: { ...(item['source'] as object), data: TOOL_IMAGE_PLACEHOLDER } };
    }
    return Object.fromEntries(
      Object.entries(item).map(([key, entry]) => [key, strip(entry, depth + 1)]),
    );
  };
  return strip(value, 0) as T;
}

/** The `file` chunks that show a tool call's images in the transcript. */
export function toolImageChunks(
  toolCallId: string,
  images: readonly ToolResultImage[],
): {
  type: 'file';
  url: string;
  mediaType: string;
  providerMetadata: { crowdex: { toolCallId: string } };
}[] {
  return images.slice(0, TOOL_IMAGES_PER_CALL).map((image) => ({
    type: 'file',
    url: `data:${image.mediaType};base64,${image.data}`,
    mediaType: image.mediaType,
    providerMetadata: { crowdex: { toolCallId } },
  }));
}

/** The tool call a `file` part came from, or null for any other part. */
export function toolImageCallId(part: {
  type: string;
  mediaType?: string;
  providerMetadata?: unknown;
}): string | null {
  if (part.type !== 'file' || part.mediaType?.startsWith('image/') !== true) return null;
  const crowdex = isRecord(part.providerMetadata) ? part.providerMetadata['crowdex'] : undefined;
  const id = isRecord(crowdex) ? crowdex['toolCallId'] : undefined;
  return typeof id === 'string' ? id : null;
}
